import { useMemo, useState } from 'react'
import EstoqueHttp from '../../../../services/api/https/estoque/Estoque.http'
import { errorHandler } from '../../../../services/util/error.service'
import { getPeriodoMesAtual, parseToMysqlFormat } from '../../../../services/util/date.service'
import { orderBy } from 'lodash'
import { useInventarioContext } from '../../../../contexts/EstoqueContext'
import { getUsuarioInfo } from '../../../../services/localstorage/localstorage.service'

const STATUS_EM_CONFERENCIA = 'Em Conferência'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  GET_INVENTARIOS: 'Carregando inventários',
  GET_ESTOQUES: 'Carregando estoques',
  DELETE: 'Apagando inventario',
}

const INITIAL_STATE = {
  inventarios: [],
  selectedInventario: null,
  periodo: getPeriodoMesAtual(),
  selectedEmpresa: null,
  modal: {
    show: false,
    data: null,
  },
  deleteModal: {
    show: false,
    data: null,
  },
  estoques: [],
  pathToRedirect: null,
  globalFilter: '',
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
}

export default function useInventario() {
  const { inventarioContext, setInventarioContext } = useInventarioContext()
  const [state, setState] = useState({
    ...INITIAL_STATE,
    periodo: inventarioContext.selectedPeriodo,
    globalFilter: inventarioContext.globalFilter,
  })

  const localDispatch = useMemo(() => {
    //Helpers

    return {
      getInventarioBySearch(selectedTp, pInicial, pFinal) {
        const periodo = {
          dataInicial: pInicial,
          dataFinal: pFinal,
        }
        setInventarioContext(prevContext => ({
          ...prevContext,
          selectedPeriodo: periodo,
        }))
        setState(prevState => ({
          ...prevState,
          periodo: periodo,
          loadingMessage: LOAD_MESSAGE.GET_INVENTARIOS,
        }))
      },

      getInventariosOnPeriodo(selectedTp, pInicial, pFinal) {
        EstoqueHttp.getInventariosOnPeriodo(
          selectedTp,
          parseToMysqlFormat(pInicial),
          parseToMysqlFormat(pFinal),
        )
          .then(res =>
            setState(prevState => ({
              ...prevState,
              inventarios: orderBy(res, ['inventario_id'], 'desc'),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      fetchEstoques(selectedTp) {
        EstoqueHttp.getEstoques(selectedTp)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              estoques: res.map(e => ({ id: e.id, name: e.name })),
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      deleteInventario(selectedTp, selectedInventarioId, pInicial, pFinal) {
        const { id: userId } = getUsuarioInfo()
        EstoqueHttp.deleteInventario(
          selectedTp,
          selectedInventarioId,
          userId,
          parseToMysqlFormat(pInicial),
          parseToMysqlFormat(pFinal),
        )
          .then(res =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              selectedInventario: null,
              inventarios: orderBy(res, ['inventario_id'], 'desc'),
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      //Actions

      onInit(selectedTp) {
        if (selectedTp) {
          this.fetchEstoques(selectedTp)
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.GET_INVENTARIOS,
          }))
        } else {
          setState(prevState => ({ ...prevState, errorMessage: 'Selecione um TP' }))
        }
      },

      onChange(name, value) {
        if (name === 'globalFilter') {
          setInventarioContext(prevContext => ({
            ...prevContext,
            globalFilter: value,
          }))
        }
        setState(prevState => {
          prevState[name] = value
          switch (name) {
            case 'selectedEmpresa':
              prevState.loadingMessage = LOAD_MESSAGE.GET_ESTADIA
              break
            case 'selectedPosicao':
              prevState.showModal = true
              break
            default:
              break
          }
          return { ...prevState }
        })
      },

      onNovo() {
        setState(prevState => ({ ...prevState, modal: { show: true, data: null } }))
      },

      onError(value) {
        setState(prevState => ({ ...prevState, errorMessage: value }))
      },

      onClose() {
        setState(prevState => ({ ...prevState, modal: { data: null, show: false } }))
      },

      onCloseDeleteModal() {
        setState(prevState => ({ ...prevState, deleteModal: { data: null, show: false } }))
      },

      onConfirmDelete(inventario) {
        setState(prevState => ({
          ...prevState,
          deleteModal: { data: null, show: false },
          selectedInventario: inventario,
          loadingMessage: LOAD_MESSAGE.DELETE,
        }))
      },

      onSave(inventarios) {
        setState(prevState => ({
          ...prevState,
          inventarios: inventarios,
          modal: { show: false, data: null },
        }))
      },

      onClickPesquisarInventario() {
        setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.GET_INVENTARIOS }))
      },

      onMenuItemClicked(item, inventario) {
        setState(prevState => {
          switch (item) {
            case 'Editar':
              prevState.modal = { show: true, data: inventario }
              break
            case 'Apagar':
              if (inventario.status === STATUS_EM_CONFERENCIA) {
                prevState.deleteModal = { show: true, data: inventario }
              } else {
                prevState.loadingMessage = LOAD_MESSAGE.DELETE
                prevState.selectedInventario = inventario
              }
              break
            case 'Associar Posições':
              setState(prevState => ({
                ...prevState,
                pathToRedirect: '/main/estoque/inventario-assoc-posicoes',
                selectedInventario: inventario,
              }))
              break
            case 'Posições Associadas':
              setState(prevState => ({
                ...prevState,
                pathToRedirect: '/main/estoque/inventario-posicoes-assoc',
                selectedInventario: inventario,
              }))
              break
            default:
              break
          }
          return { ...prevState }
        })
      },

      resetState() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },

      getMenuItems() {
        return ['Editar', 'Apagar', 'Associar Posições', 'Posições Associadas']
      },
    }
  }, [])

  return { state, localDispatch }
}
